import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Search, Plus, Pencil, Trash2, Loader2, CheckCircle2, Printer, Copy } from 'lucide-react';
import FlashingProfileSVG from '@/components/FlashingProfileSVG';
import FlashingTraceEditor from '@/components/FlashingTraceEditor';

/**
 * Flashing Template Library — reusable named flashing profiles (Apron,
 * Barge, Box Gutter, Soaker, Valley, Parapet, Drip Edge, custom).
 *
 * Operators can:
 *   - Browse all templates grouped by category, with a SVG preview thumb.
 *   - Create a new template (Visual Builder with live SVG preview).
 *   - Edit folds / start angle / notes on existing templates.
 *   - Delete custom templates (seed templates stay; operator may still
 *     duplicate them by saving a new copy with edits).
 *
 * Loaded into Appraisals via FlashingTemplatePicker (separate component
 * inside the FlashingFoldsPanel).
 */
const CATEGORIES = ['Apron', 'Barge', 'Box Gutter', 'Soaker', 'Valley', 'Parapet', 'Drip Edge', 'Back Tray', 'Custom'];

export default function FlashingTemplatesPage() {
  const { api } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // template doc or 'new'
  const [savingId, setSavingId] = useState(null);
  const [flash, setFlash] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/flashing-templates' + (filterCat ? `?category=${encodeURIComponent(filterCat)}` : ''));
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [api, filterCat]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((t) =>
      [t.name, t.category, t.notes].some((v) => (v || '').toLowerCase().includes(q))
    );
  }, [items, search]);

  // Lookup of category → custom starter template (the one the operator
  // promoted via "Save as starter for X"). The trace editor uses this
  // map to override its built-in seed shapes so the office's preferred
  // backtray / apron / barge layouts load by default for new templates.
  const categoryStarters = useMemo(() => {
    const out = {};
    items.forEach((t) => {
      if (t.is_category_starter && t.category) {
        out[t.category] = { start_angle_deg: t.start_angle_deg, folds: t.folds };
      }
    });
    return out;
  }, [items]);

  const handleSetAsStarter = useCallback(async (tplId, category) => {
    try {
      await api.post(`/flashing-templates/${tplId}/set-as-starter`);
      setFlash({ ok: true, msg: `Set as default starter for ${category}.` });
      setTimeout(() => setFlash(null), 3500);
      await load();
      // Keep the editor open with the updated `is_category_starter` flag
      setEditing((cur) => cur && cur._id === tplId ? { ...cur, is_category_starter: true } : cur);
    } catch (e) {
      setFlash({ ok: false, msg: e?.response?.data?.detail || 'Failed to set as starter' });
      setTimeout(() => setFlash(null), 4000);
    }
  }, [api, load]);

  const handleUnsetAsStarter = useCallback(async (tplId) => {
    try {
      await api.post(`/flashing-templates/${tplId}/unset-as-starter`);
      setFlash({ ok: true, msg: 'Reverted to built-in starter shape.' });
      setTimeout(() => setFlash(null), 3500);
      await load();
      setEditing((cur) => cur && cur._id === tplId ? { ...cur, is_category_starter: false } : cur);
    } catch (e) {
      setFlash({ ok: false, msg: e?.response?.data?.detail || 'Failed to revert' });
      setTimeout(() => setFlash(null), 4000);
    }
  }, [api, load]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((t) => {
      const k = t.category || 'Custom';
      (map[k] = map[k] || []).push(t);
    });
    return map;
  }, [filtered]);

  // Open the editor for a brand-new template. When `cat` is supplied
  // (e.g. from a category-heading "+ New Apron" button) we pre-fill the
  // category dropdown AND auto-load the matching starter shape if one
  // exists, so the operator starts from a known-good baseline.
  const openNew = (cat) => {
    const category = cat || 'Custom';
    const starter = (categoryStarters || {})[category];
    setEditing({
      name: '',
      category,
      notes: '',
      start_angle_deg: starter?.start_angle_deg || 0,
      folds: starter?.folds ? JSON.parse(JSON.stringify(starter.folds)) : [],
      sheet_profiles: [],
      pipe_usage_links: [],
      edges: { start: null, end: null },
    });
  };

  // Open the editor populated with an existing template's geometry as
  // a fresh draft (no _id → save creates a new doc). Name is suffixed
  // with " (copy)" so the duplicate-prevention check on the backend
  // doesn't fire. Operator can rename before saving.
  const openDuplicate = (tpl) => {
    setEditing({
      name: `${tpl.name} (copy)`,
      category: tpl.category || 'Custom',
      notes: tpl.notes || '',
      start_angle_deg: tpl.start_angle_deg || 0,
      folds: JSON.parse(JSON.stringify(tpl.folds || [])),
      sheet_profiles: [...(tpl.sheet_profiles || [])],
      pipe_usage_links: JSON.parse(JSON.stringify(tpl.pipe_usage_links || [])),
      edges: { ...(tpl.edges || { start: null, end: null }) },
      primary_editable_folds: [...(tpl.primary_editable_folds || [])],
      label_offsets: JSON.parse(JSON.stringify(tpl.label_offsets || [])),
    });
  };

  const handleDelete = async (tpl) => {
    if (!window.confirm(`Delete "${tpl.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/flashing-templates/${tpl._id}`);
      setFlash({ kind: 'ok', text: `Deleted "${tpl.name}"` });
      load();
    } catch (e) {
      setFlash({ kind: 'err', text: e.response?.data?.detail || 'Delete failed' });
    }
  };

  const handleSave = async (payload) => {
    setSavingId(payload._id || 'new');
    try {
      if (payload._id) {
        await api.put(`/flashing-templates/${payload._id}`, {
          name: payload.name,
          category: payload.category,
          sheet_profiles: payload.sheet_profiles || (payload.sheet_profile ? [payload.sheet_profile] : []),
          pipe_usage_links: payload.pipe_usage_links || [],
          edges: payload.edges || { start: null, end: null },
          primary_editable_folds: payload.primary_editable_folds || [],
          notes: payload.notes,
          start_angle_deg: payload.start_angle_deg,
          folds: payload.folds,
          label_offsets: payload.label_offsets || [],
        });
        setFlash({ kind: 'ok', text: `Saved "${payload.name}"` });
      } else {
        await api.post('/flashing-templates', payload);
        setFlash({ kind: 'ok', text: `Created "${payload.name}"` });
      }
      setEditing(null);
      load();
    } catch (e) {
      setFlash({ kind: 'err', text: e.response?.data?.detail || 'Save failed' });
    } finally {
      setSavingId(null);
    }
  };

  // "Save as new" — POSTs a brand-new template from the editor's
  // current state, leaves the source template untouched, and switches
  // the editor to the new doc so the operator can continue tweaking.
  const handleSaveAsNew = useCallback(async (payload) => {
    setSavingId('new');
    try {
      const body = {
        name: payload.name,
        category: payload.category,
        sheet_profiles: payload.sheet_profiles || [],
        pipe_usage_links: payload.pipe_usage_links || [],
        edges: payload.edges || { start: null, end: null },
        primary_editable_folds: payload.primary_editable_folds || [],
        notes: payload.notes,
        start_angle_deg: payload.start_angle_deg,
        folds: payload.folds,
        label_offsets: payload.label_offsets || [],
      };
      const { data } = await api.post('/flashing-templates', body);
      setFlash({ kind: 'ok', text: `Created "${data.name}" as a new template` });
      await load();
      setEditing(data);  // switch editor to the new doc
    } catch (e) {
      setFlash({ kind: 'err', text: e.response?.data?.detail || 'Save-as-new failed' });
    } finally {
      setSavingId(null);
    }
  }, [api, load]);

  const handlePrintCatalogue = useCallback(async () => {
    // Stream the Flashing Library catalogue PDF in a new tab. Auth header
    // is required on every API call, so we fetch the blob via the `api`
    // axios instance and pop it open via an object URL — same pattern the
    // other PDF download buttons use.
    try {
      const url = '/flashing-templates/catalogue.pdf' + (filterCat ? `?category=${encodeURIComponent(filterCat)}` : '');
      const { data } = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([data], { type: 'application/pdf' });
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (e) {
      // 404 = empty library; surface detail to the operator.
      let detail = 'Failed to generate catalogue PDF';
      try {
        if (e?.response?.data instanceof Blob) {
          const txt = await e.response.data.text();
          const j = JSON.parse(txt);
          if (j?.detail) detail = j.detail;
        } else if (e?.response?.data?.detail) {
          detail = e.response.data.detail;
        }
      } catch (_) {/* ignore parse error */}
      setFlash({ kind: 'err', text: detail });
    }
  }, [api, filterCat]);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="flashing-templates-page">
      <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A]">Flashing Templates</h1>
          <p className="text-sm text-slate-500 mt-1">Reusable named flashing profiles — load into any appraisal's Flashing component to set the folds and girth instantly.</p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href="/admin/flashing-pipe-mapping"
            data-testid="flashing-pipe-mapping-link"
            className="inline-flex items-center gap-2 px-3 py-2 bg-white hover:bg-emerald-50 text-emerald-700 text-sm font-semibold rounded-md shadow-sm ring-1 ring-emerald-300 transition-colors"
            title="Edit the centralized Pipe → Template auto-link mapping table"
          >
            🔗 Pipe mapping
          </a>
          <button
            data-testid="flashing-template-print-catalogue-btn"
            onClick={handlePrintCatalogue}
            className="inline-flex items-center gap-2 px-3 py-2 bg-white hover:bg-slate-50 text-slate-700 text-sm font-semibold rounded-md shadow-sm ring-1 ring-slate-300 transition-colors"
            title={filterCat ? `Print catalogue · ${filterCat} only` : 'Print full library catalogue'}
          >
            <Printer size={16} /> Print catalogue
          </button>
          <button
            data-testid="flashing-template-new-btn"
            onClick={openNew}
            className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-md shadow-sm transition-colors"
          >
            <Plus size={16} /> New template
          </button>
        </div>
      </header>

      {flash && (
        <div className={`mb-4 px-3 py-2 rounded text-sm flex items-center gap-2 ${flash.kind === 'ok' ? 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200' : 'bg-rose-50 text-rose-800 ring-1 ring-rose-200'}`} data-testid="flashing-template-flash">
          {flash.kind === 'ok' && <CheckCircle2 size={14} />} {flash.text}
          <button className="ml-auto text-xs underline" onClick={() => setFlash(null)}>dismiss</button>
        </div>
      )}

      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <div className="relative">
          <Search className="absolute left-2 top-2.5 text-slate-400" size={14} />
          <input
            data-testid="flashing-template-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name / notes…"
            className="pl-7 pr-3 py-2 text-sm border border-[#E2E2DF] rounded-md w-64 bg-white"
          />
        </div>
        <select
          data-testid="flashing-template-cat-filter"
          value={filterCat}
          onChange={(e) => setFilterCat(e.target.value)}
          className="text-sm px-2 py-2 border border-[#E2E2DF] rounded-md bg-white"
        >
          <option value="">All categories</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <span className="text-xs text-slate-500" data-testid="flashing-template-count">
          {filtered.length} template{filtered.length === 1 ? '' : 's'}
        </span>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 size={14} className="animate-spin" /> Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-slate-500 italic px-4 py-8 bg-slate-50 rounded">No flashing templates match — create your first one with "New template".</div>
      ) : (
        Object.keys(grouped).sort().map((cat) => (
          <section key={cat} className="mb-8">
            <h2 className="text-sm font-bold uppercase tracking-wider text-emerald-700 mb-2 flex items-center gap-2">
              <span>{cat} <span className="text-slate-400 font-normal">· {grouped[cat].length}</span></span>
              <button
                type="button"
                data-testid={`flashing-template-new-in-${cat}`}
                onClick={() => openNew(cat)}
                className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 bg-white ring-1 ring-emerald-300 hover:bg-emerald-50 rounded-full normal-case tracking-normal"
                title={`Start a new ${cat} flashing template`}
              >
                <Plus size={11} /> New {cat}
              </button>
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
              {grouped[cat].map((tpl) => {
                const foldCount = Math.max(0, (tpl.folds || []).length - 1);
                const girth = (tpl.folds || []).reduce((s, f) => s + (Number(f.length_mm) || 0), 0);
                return (
                  <div key={tpl._id} className="bg-white rounded-lg border border-[#E2E2DF] p-2 hover:border-emerald-300 transition-colors" data-testid={`flashing-template-card-${tpl._id}`}>
                    <FlashingProfileSVG
                      folds={tpl.folds}
                      startAngle={tpl.start_angle_deg}
                      width={170}
                      height={80}
                      labelsLayout="right-column"
                      edges={tpl.edges}
                      edgeConfigs={tpl.edge_configs}
                    />
                    <div className="mt-2 flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-[#0F172A] truncate" title={tpl.name}>{tpl.name}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5 font-mono-num" data-testid={`flashing-template-fold-count-${tpl._id}`}>{foldCount} fold{foldCount === 1 ? '' : 's'} · girth {girth}mm</div>
                        {/* Profile chips — multi-profile pairing badges */}
                        {Array.isArray(tpl.sheet_profiles) && tpl.sheet_profiles.filter(Boolean).length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1" data-testid={`flashing-template-profiles-${tpl._id}`}>
                            {tpl.sheet_profiles.filter(Boolean).map((p) => (
                              <span key={p} className="text-[9px] font-semibold text-sky-700 bg-sky-50 ring-1 ring-sky-200 rounded px-1.5 py-0.5">{p}</span>
                            ))}
                          </div>
                        )}
                        {Array.isArray(tpl.pipe_usage_links) && tpl.pipe_usage_links.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1" data-testid={`flashing-template-pipe-links-${tpl._id}`}>
                            {tpl.pipe_usage_links.map((l, i) => (
                              <span key={i} className="text-[9px] font-semibold text-violet-700 bg-violet-50 ring-1 ring-violet-200 rounded px-1.5 py-0.5">
                                {l.usage?.replace(/_/g, ' ')}{l.pipe_dia_mm ? ` · ${l.pipe_dia_mm}mm` : ''}
                              </span>
                            ))}
                          </div>
                        )}
                        {(tpl.edges?.start || tpl.edges?.end) && (
                          <div className="mt-1 flex flex-wrap gap-1" data-testid={`flashing-template-edges-${tpl._id}`}>
                            {tpl.edges?.start && (
                              <span className="text-[9px] font-semibold text-teal-700 bg-teal-50 ring-1 ring-teal-200 rounded px-1.5 py-0.5">
                                start · {tpl.edges.start}
                              </span>
                            )}
                            {tpl.edges?.end && (
                              <span className="text-[9px] font-semibold text-teal-700 bg-teal-50 ring-1 ring-teal-200 rounded px-1.5 py-0.5">
                                end · {tpl.edges.end}
                              </span>
                            )}
                          </div>
                        )}
                        {Array.isArray(tpl.primary_editable_folds) && tpl.primary_editable_folds.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1" data-testid={`flashing-template-editable-folds-${tpl._id}`}>
                            {tpl.primary_editable_folds.map((idx) => (
                              <span key={idx} className="text-[9px] font-semibold text-amber-700 bg-amber-50 ring-1 ring-amber-300 rounded px-1.5 py-0.5">
                                ⚙ F{idx + 1} editable
                              </span>
                            ))}
                          </div>
                        )}
                        {tpl.is_seed && <span className="inline-block mt-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-amber-50 text-amber-700 ring-1 ring-amber-200 rounded mr-1">Seed</span>}
                        {tpl.is_category_starter && <span data-testid={`flashing-template-starter-badge-${tpl._id}`} className="inline-block mt-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 rounded">★ Default starter</span>}
                      </div>
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => setEditing(tpl)}
                          data-testid={`flashing-template-edit-${tpl._id}`}
                          className="p-1.5 text-slate-500 hover:text-emerald-700 hover:bg-emerald-50 rounded transition-colors"
                          title="Edit"
                        ><Pencil size={14} /></button>
                        <button
                          onClick={() => openDuplicate(tpl)}
                          data-testid={`flashing-template-duplicate-${tpl._id}`}
                          className="p-1.5 text-slate-500 hover:text-sky-700 hover:bg-sky-50 rounded transition-colors"
                          title="Make a new template based on this one (opens editor in design mode for review)"
                        ><Copy size={14} /></button>
                        <button
                          onClick={() => handleDelete(tpl)}
                          data-testid={`flashing-template-delete-${tpl._id}`}
                          className="p-1.5 text-slate-500 hover:text-rose-700 hover:bg-rose-50 rounded transition-colors"
                          title="Delete"
                        ><Trash2 size={14} /></button>
                      </div>
                    </div>
                    {tpl.notes && <div className="mt-1 text-[11px] text-slate-500 italic line-clamp-2">{tpl.notes}</div>}
                  </div>
                );
              })}
            </div>
          </section>
        ))
      )}

      {editing && (
        <FlashingTraceEditor
          initial={editing}
          saving={savingId === (editing._id || 'new')}
          api={api}
          categoryStarters={categoryStarters}
          onCancel={() => setEditing(null)}
          onSave={handleSave}
          onSaveAsNew={handleSaveAsNew}
          onSetAsStarter={handleSetAsStarter}
          onUnsetAsStarter={handleUnsetAsStarter}
        />
      )}
    </div>
  );
}

